/**
 * Settings that live on this machine.
 *
 * Kept local rather than on the account because each is about this
 * installation — what this copy of NevTan offers and how it runs models — and
 * syncing them would make one machine's hardware dictate another's.
 */

const KEY = 'nevtan.preferences'

export interface Preferences {
  /** Offer hosted models alongside the ones on this machine. */
  cloudModels: boolean
  /**
   * How much conversation a local model is given, in tokens.
   *
   * Larger costs memory: the engine reserves it whether or not the
   * conversation fills it, which is why this is a choice and not a maximum.
   */
  contextTokens: number
  /** Check whether installed models have been superseded upstream. */
  checkModelUpdates: boolean
}

export const DEFAULTS: Preferences = {
  cloudModels: true,
  contextTokens: 8192,
  checkModelUpdates: true,
}

/** The sizes offered, matching what the engines and weights actually support. */
export const CONTEXT_CHOICES = [4096, 8192, 16384, 32768, 65536, 131072] as const

export function loadPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return { ...DEFAULTS }
    // Merged over the defaults so a preference added later has a value on a
    // machine that stored the settings before it existed.
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Preferences>) }
  } catch {
    return { ...DEFAULTS }
  }
}

export function savePreferences(preferences: Preferences): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(preferences))
  } catch {
    // A browser refusing storage costs the setting, not the session.
  }
}

export function resetPreferences(): Preferences {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Nothing to undo if it was never stored.
  }
  return { ...DEFAULTS }
}
